import {
  Animated,
  View,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  Modal,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { ReactNode } from 'react';
import { Directions, FlingGestureHandler } from 'react-native-gesture-handler';
import {Border, Breakpoints, Color, Padding} from '@/GlobalStyles';
import { useCardAnimation } from '@react-navigation/stack';
import { useIsFocused } from '@react-navigation/native';
import { Image } from 'expo-image';

type ModalScreenProps = {
  children?: ReactNode | undefined;
  showCloseButton?: boolean;
  withBottomPadding?: boolean;
  maxWidth?: number;
  padding?: number;
};

export const useModalScreenPosition = (maxWidth: number) => {
  const { width, height } = useWindowDimensions();

  const landscape = width > Breakpoints.tablet;

  const widthOverlayPerc = 0.25;
  const heightOverlayPerc = 0.15;

  const x = {
    from: width,
    to: width - Math.min(maxWidth, width * (1 - widthOverlayPerc)),
  };

  const y = {
    from: height,
    to: height * heightOverlayPerc,
  };

  return {
    landscape,
    height: landscape ? height : height * (1 - heightOverlayPerc),
    width: landscape ? Math.min(maxWidth, width * (1 - widthOverlayPerc)) : width,
    x,
    y,
  };
};

const ModalScreen = (props: ModalScreenProps) => {
  const {
    children,
    showCloseButton = true,
    withBottomPadding = false,
    maxWidth = 1024,
    padding = Padding.p_13xl,
  } = props;
  const router = useRouter();
  const { current } = useCardAnimation();

  const visible = useIsFocused();

  const { landscape, height, width, x, y } = useModalScreenPosition(maxWidth);

  // const landscape = width > height;

  return (
    <FlingGestureHandler
      direction={landscape ? Directions.RIGHT : Directions.DOWN}
      onEnded={() => router.canGoBack()}
    >
      <Animated.View>
        <Modal
          visible={Platform.OS !== 'ios' || visible}
          transparent
          animationType={'fade'}
          supportedOrientations={['landscape', 'portrait']}
        >
          <Animated.View
            style={{
              flex: 1,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            <Pressable
              style={[StyleSheet.absoluteFill, { backgroundColor: Color.sideOverlayBg }]}
              onPress={() => {
                console.log('back');
                router.back();
              }}
            />
            <Animated.View
              style={[
                {
                  height,
                  width,
                  transform: [
                    landscape
                      ? {
                          translateX: current.progress.interpolate({
                            inputRange: [0, 1],
                            outputRange: [x.from, x.to],
                            extrapolate: 'clamp',
                          }),
                        }
                      : {
                          translateY: current.progress.interpolate({
                            inputRange: [0, 1],
                            outputRange: [y.from, y.to],
                            extrapolate: 'clamp',
                          }),
                        },
                  ],
                },
              ]}
            >
              <View
                style={[
                  styles.viewContainer,
                  { padding },
                  withBottomPadding ? { paddingBottom: padding } : { paddingBottom: 0 },
                ]}
              >
                {showCloseButton ? (
                  <Pressable style={{ zIndex: 999 }} onPress={() => router.back()}>
                    <View
                      style={[
                        styles.closeButton,
                        landscape
                          ? { left: -30 - padding, top: 20 - padding }
                          : { top: -30 - padding, right: 20 - padding },
                      ]}
                    >
                      <Image
                        style={styles.closeButtonImage}
                        source={require('@assets/icones/cross.png')}
                      />
                    </View>
                  </Pressable>
                ) : null}
                {children}
              </View>
            </Animated.View>
          </Animated.View>
        </Modal>
      </Animated.View>
    </FlingGestureHandler>
  );
};

export default ModalScreen;

const styles = StyleSheet.create({
  viewContainer: {
    flex: 1,
    paddingBottom: 0,
    backgroundColor: Color.blanc,
    position: 'relative',
  },
  closeButton: {
    backgroundColor: Color.MainBleu,
    height: 48,
    width: 48,
    borderRadius: Border.br_3xs,
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonImage: {
    height: 22,
    width: 22,
  },
});
